import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import z from "zod";

import BackButton from "@/components/back-button";
import { captureEvent } from "@/lib/analytics/posthog";
import { useInfiniteWorkerNearbyQuery } from "@/queries/hooks/workerDashboardHooks";
import { cn } from "@/utils";

import { Row } from "../dashboard/-components/worker-v2/nearby-shifts";
import { rangeFor } from "../dashboard/-components/worker-v2/range-picker";

// Carries the dashboard's "Available near you" filters so the full list matches its count.
const SearchSchema = z.object({
  start: z.string().optional(),
  end: z.string().optional(),
  radius: z.coerce.number().optional(),
  places: z.enum(["all", "mine"]).optional(),
});

export const Route = createFileRoute("/app/$role/_app/available/")({
  validateSearch: SearchSchema,
  component: AvailableShiftsPage,
});

function AvailableShiftsPage() {
  const { t } = useTranslation("dashboard");
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const fallback = rangeFor("next7");
  const start = search.start ?? fallback.start;
  const end = search.end ?? fallback.end;
  const radius = search.radius ?? 5;
  const places = search.places ?? "all";

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteWorkerNearbyQuery({ start, end, radiusMi: radius, places });

  const items = data?.pages.flatMap((p) => p.items) ?? [];
  const first = data?.pages[0];

  const chip = (value: "all" | "mine", label: string, count: number | undefined) => (
    <button
      type="button"
      onClick={() => {
        navigate({ search: (prev) => ({ ...prev, places: value }), replace: true });
        captureEvent("nearby_places_toggled", { mine: value === "mine", source: "see_all" });
      }}
      className={cn(
        "h-8 px-3 rounded-full text-xs font-semibold",
        places === value ? "bg-garnet text-white" : "bg-fog text-garnet hover:bg-fog-dark/30",
      )}
    >
      {label}
      {count != null ? ` · ${count}` : ""}
    </button>
  );

  return (
    <div className="flex flex-col w-full px-3 sm:px-6 md:px-8 pt-4 sm:pt-6 md:pt-8 pb-16 max-w-[820px] mx-auto">
      <BackButton />
      <div className="flex items-center justify-between gap-3 mt-4 mb-4 flex-wrap">
        <div>
          <h1 className="heading-1">{t("workerV2.nearby.allTitle")}</h1>
          <p className="text-sm text-slate">
            {`${format(parseISO(start), "MMM d")} – ${format(parseISO(end), "MMM d")}`} ·{" "}
            {t("workerV2.nearby.within", { mi: radius })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {chip("all", t("workerV2.nearby.all"), first?.total)}
          {chip("mine", t("workerV2.nearby.myPlaces"), first?.mine_count)}
        </div>
      </div>

      <section className="bg-white rounded-[15px] p-4 sm:p-5">
        {isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-12 rounded-lg bg-fog animate-pulse" />
            ))}
          </div>
        ) : isError ? (
          <div className="flex items-center gap-3 text-sm text-slate">
            {t("v2.errorSection")}
            <button type="button" onClick={() => refetch()} className="underline text-teal">
              {t("v2.retry")}
            </button>
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate py-8 text-center">
            {t("workerV2.nearby.empty", { radius })}
          </p>
        ) : (
          <>
            <ul className="flex flex-col divide-y divide-[#545A63]/15">
              {items.map((s, i) => (
                <Row
                  key={s.shift_id}
                  shift={s}
                  index={i}
                  selected={false}
                  onSelect={() => {}}
                  onHover={() => {}}
                />
              ))}
            </ul>
            <div className="pt-4 flex justify-center">
              {hasNextPage ? (
                <button
                  type="button"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="h-10 px-5 rounded-lg border border-fog-dark text-sm text-garnet hover:bg-fog disabled:opacity-50"
                >
                  {isFetchingNextPage ? t("v2.loading") : t("v2.loadMore")}
                </button>
              ) : (
                <span className="text-xs text-slate">{t("workerV2.nearby.end")}</span>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
